import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { AuthForms } from '~/components/landing/AuthForms';
import { LandingView } from '~/components/landing/LandingView';
import { useAnalytics } from '~/hooks/useAnalytics';
import { isAuthenticated } from '~/lib/auth';
import type { PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/auth')({
  component: AuthPage,
});

function AuthPage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug, landingPageId: landingPage.id });

  // If already authenticated, redirect immediately
  useEffect(() => {
    if (isAuthenticated()) {
      navigateAfterAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateAfterAuth = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  const handleAuthSuccess = useCallback(
    (mode?: 'register' | 'login') => {
      if (mode === 'register') {
        trackEvent('registration_complete');
      } else if (mode === 'login') {
        trackEvent('login_complete');
      }
      navigateAfterAuth();
    },
    [trackEvent, navigateAfterAuth],
  );

  return (
    <LandingView landingPage={landingPage}>
      <AuthForms slug={slug} onSuccess={handleAuthSuccess} />
    </LandingView>
  );
}
